import type { ProductCard } from '@common/types/product-card.type';
import { ProductType, ProductUnits } from '@common/types/product-card.type';
import type { SelectedFileExtended } from '@common/types/file.type';
// eslint-disable-next-line @nx/enforce-module-boundaries
import type { CatalogProductCard } from '@web/widgets/product-card/type';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { CatalogProductType } from '@web/widgets/product-card/type';
import type { SelectedFileInterface } from '@sendpulse/file-manager';
import { extendWebFileData, extendWebFileDataPromise, preparedSelectedFile } from './file';

export const CATALOG_UNITS_MAP: Record<number, ProductUnits> = {
    1: ProductUnits.pieces,
    2: ProductUnits.service,
    3: ProductUnits.meter,
    4: ProductUnits.kit,
    5: ProductUnits.l,
    6: ProductUnits.kg,
};

export const CRM_PRODUCT_BLOCK_DISPLAY_ID = 'CRM_PRODUCT_BLOCK';
export const CRM_PRODUCT_WIDGET_DISPLAY_ID = 'CRM_PRODUCT_WIDGET';

export function mapCatalogProductToCard(
    catalogProduct: CatalogProductCard,
    images: SelectedFileExtended[] = []
): ProductCard {
    return {
        name: catalogProduct.name,
        description: catalogProduct.description,
        shortDescription: catalogProduct.shortDescription,
        price: computeProductPrice(catalogProduct),
        oldPrice: catalogProduct.prices[0]?.oldValue ?? null,
        images,
        sections: (catalogProduct.sections || []).map((section) => ({
            name: section.name,
            parameters: section.values.map((param) => ({
                parameter: param.key,
                value: param.value,
            })),
        })),
        vendorCode: catalogProduct.vendorCode || '',
        quantity: catalogProduct.productType === CatalogProductType.physical ? catalogProduct.balance.value : null,
        unit: CATALOG_UNITS_MAP[catalogProduct.balance.type] || ProductUnits.pieces,
        id: catalogProduct.id.toString(),
        type: catalogProduct.productType === CatalogProductType.physical ? ProductType.physical : ProductType.digital,
        variants: null,
        properties: null,
        catalogProductId: catalogProduct.id,
        digitalProductId: null,
        catalogCategoryName: catalogProduct.categoryName ?? '',
        visible: catalogProduct.visible,
        subscriptionPeriod: catalogProduct.recurringMode,
        dimensions: catalogProduct.dimensions || null,
    };
}

export function computeProductPrice(product: CatalogProductCard): number {
    const base = Number(product?.prices?.[0]?.value ?? 0);
    if (!base) return 0;

    const shouldApplyTax =
        Array.isArray(product?.taxes) && product.taxes.length > 0 && !product.taxes[0].isAddedToAmount;

    const taxPercent = shouldApplyTax ? Number(product.taxes[0].value ?? 0) : 0;
    const result = shouldApplyTax ? base * (1 + taxPercent / 100) : base;

    const factor = 100;
    return Math.round((result + Number.EPSILON) * factor) / factor;
}

export function prepareImages(images: string[], userHash: string, cdn: string): SelectedFileInterface[] {
    return (images ?? []).map((path) => preparedSelectedFile(path, userHash, cdn));
}

export function prepareCatalogImages(images: string[], userHash: string, cdn: string): SelectedFileExtended[] {
    return prepareImages(images, userHash, cdn).map(extendWebFileData);
}

export async function prepareCatalogImagesAsync(
    images: string[],
    userHash: string,
    cdn: string
): Promise<SelectedFileExtended[]> {
    const prepared = prepareImages(images, userHash, cdn);
    const extended: SelectedFileExtended[] = [];
    for (const file of prepared) {
        const extendedFile = await extendWebFileDataPromise(file, cdn);
        extended.push(extendedFile);
    }
    return extended;
}
